import '@f-fertighaus/layout/PageHeader/SSR/PageHeaderSSR'

import { CacheProvider } from '@emotion/react'
import { ScopedCssBaseline } from '@mui/material'
import React, { useCallback, useEffect } from 'react'
import { hydrateRoot } from 'react-dom/client'

import { MuiThemeProvider } from '@f-fertighaus/applications/Next/config/MuiThemeProvider'
import { createEmotionCache } from '@f-fertighaus/applications/Next/config/styles'
import { HDPContent } from '@f-fertighaus/applications/Next/pages/house-detail-page/HDPContent'

const loadRequestFlowTriggersHDP = () =>
  import('@js/common/request-flow-triggers-HDP')
const loadPageHeader = () =>
  import('@f-fertighaus/layout/PageHeader/SSR/PageHeaderSSR')
const loadSiteWide = () => import('@js/common/site-wide')
const loadFloatingBanner = () => import('@f-fertighaus/widgets/FloatingBanner')
const loadTracking = () => import('@js/common/_wt')

import '../../css/ancient-css/house-detail/house-detail.css'
import '@f/components/Banner/Banner.css'
import '@f/widgets/CookieBanner/CookieBanner.css'
import '@f/widgets/CookieModal/CookieModal.css'

const ScriptLoader = ({ children }) => {
  const [pageHeaderLoaded, setPageHeaderLoaded] = React.useState(false)
  const [globalScriptsLoaded, setGlobalScriptsLoaded] = React.useState(false)

  const pageHeaderWrapperRef = React.useRef(null)

  const handlePageHeaderLoad = useCallback(() => {
    loadPageHeader()
    setPageHeaderLoaded(true)
  }, [])

  useEffect(() => {
    pageHeaderWrapperRef.current = document.querySelector(
      '.js-page-header-wrapper'
    )

    if (pageHeaderWrapperRef.current) {
      pageHeaderWrapperRef.current.addEventListener(
        'mouseover',
        handlePageHeaderLoad
      )
      pageHeaderWrapperRef.current.addEventListener(
        'touchstart',
        handlePageHeaderLoad
      )
    }

    const handleGlobalScriptsLoad = () => {
      loadSiteWide()
      loadFloatingBanner()
      loadRequestFlowTriggersHDP()
      loadTracking()
      setGlobalScriptsLoaded(true)
    }

    if (document.readyState === 'complete') {
      handleGlobalScriptsLoad()
    } else {
      window.addEventListener('load', handleGlobalScriptsLoad)
    }

    if (globalScriptsLoaded) {
      window.removeEventListener('load', handleGlobalScriptsLoad)
    }
  }, [handlePageHeaderLoad, globalScriptsLoaded])

  useEffect(() => {
    if (pageHeaderLoaded) {
      pageHeaderWrapperRef.current.removeEventListener(
        'mouseover',
        handlePageHeaderLoad
      )
      pageHeaderWrapperRef.current.removeEventListener(
        'touchstart',
        handlePageHeaderLoad
      )
    }
  }, [pageHeaderLoaded, handlePageHeaderLoad])

  return children
}

const hydrateHdpApp = () => {
  const container = window.mountApp
  const cache = createEmotionCache()

  hydrateRoot(
    container,
    <CacheProvider value={cache}>
      <MuiThemeProvider domain={window.hdpPageProps.domain}>
        <ScopedCssBaseline>
          <ScriptLoader>
            <HDPContent
              houseData={window.hdpPageProps.houseData}
              vendorData={window.hdpPageProps.vendorData}
              domain={window.hdpPageProps.domain}
              reviewsActive={window.hdpPageProps.reviewsActive}
              chatAssistantActive={window.hdpPageProps.chatAssistantActive}
              featureFlags={window.hdpPageProps.featureFlags}
            />
          </ScriptLoader>
        </ScopedCssBaseline>
      </MuiThemeProvider>
    </CacheProvider>
  )
}

if (window.mountApp) {
  hydrateHdpApp()
} else {
  window.addEventListener('load', hydrateHdpApp)
}
